import React from 'react';
import { Link } from 'react-router-dom';
import ListingCard from '../components/ListingCard';
import ListingsCarousel from '../components/ListingsCarousel';
import Loading from '../components/Loading';
import useScrollAnimation from '../hooks/useScrollAnimation';
import useListings from '../hooks/useListings';
import siteContent from '../content';

const { hero, active, sold, cta } = siteContent.pages.listings.sections;

const Listings: React.FC = () => {
  const listingsSection = useScrollAnimation({ threshold: 0 });
  const { listings: activeListings, isLoading: activeLoading } = useListings({ type: 'active', useMockFallback: false });
  const { listings: soldListings, isLoading: soldLoading } = useListings({ type: 'sold', useMockFallback: false });

  return (
    <>
      <title>Property Listings | Kline Real Estate Works</title>
      <meta
        name="description"
        content="Browse our current and recently sold property listings in Royersford, PA and surrounding areas. Find your next home with Kline Real Estate Works."
      />
      <meta property="og:title" content="Property Listings | Kline Real Estate Works" />
      <meta property="og:description" content="Browse our current and recently sold property listings in Royersford, PA and surrounding areas. Find your next home with Kline Real Estate Works." />
      <meta property="og:image" content="https://keithklinegroup.com/og-image.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content="https://keithklinegroup.com/og-image.jpg" />

      {/* Hero Section */}
      <section
        className="hero hero-with-image"
        data-cms-page="listings"
        data-cms-section="hero"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 43, 92, 0.75), rgba(0, 43, 92, 0.85)), url('${hero.image}')`
        }}
        data-cms-field="image"
        data-cms-type="image"
      >
        <div className="hero-content">
          <p className="hero-subtitle" data-cms-field="subtitle">{hero.subtitle}</p>
          <h1 data-cms-field="heading">{hero.heading}</h1>
          <p data-cms-field="description">{hero.description}</p>
        </div>
      </section>

      {/* Active Listings Section */}
      <section className="section" data-cms-page="listings" data-cms-section="active">
        <div className="container">
          <div className="section-header">
            <h2 data-cms-field="heading">{active.heading}</h2>
            <p data-cms-field="subheading">{active.subheading}</p>
          </div>
          {activeLoading ? (
            <Loading message="Loading available properties..." />
          ) : activeListings.length > 0 ? (
            <div className="grid-3-centered">
              {activeListings.map((listing) => (
                <ListingCard key={listing.id} listing={listing} />
              ))}
            </div>
          ) : (
            <div
              style={{
                backgroundColor: 'var(--color-gray-100)',
                padding: '3rem',
                borderRadius: '12px',
                textAlign: 'center'
              }}
              data-cms-page="listings"
              data-cms-section="active"
            >
              <span
                className="material-icons"
                style={{ fontSize: '4rem', color: 'var(--color-gold)', marginBottom: '1rem', display: 'block' }}
                data-cms-field="emptyState.icon"
                data-cms-field-type="icon"
              >
                {active.emptyState.icon}
              </span>
              <h3 data-cms-field="emptyState.heading">{active.emptyState.heading}</h3>
              <p style={{ maxWidth: '500px', margin: '0 auto 1.5rem', color: 'var(--color-gray-600)' }} data-cms-field="emptyState.description">
                {active.emptyState.description}
              </p>
              <Link
                to={active.emptyState.ctaLink}
                className="btn btn-primary"
                data-cms-field="emptyState.ctaText"
                data-cms-link-field="emptyState.ctaLink"
              >
                {active.emptyState.ctaText}
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Recently Sold Section */}
      <section className="section section-bg" data-cms-page="listings" data-cms-section="sold">
        <div className="container">
          <div
            ref={listingsSection.ref}
            className={`scroll-fade-up ${listingsSection.isVisible ? 'visible' : ''}`}
          >
            <div className="section-header">
              <h2 data-cms-field="heading">{sold.heading}</h2>
              <p data-cms-field="subheading">{sold.subheading}</p>
            </div>
            {soldLoading ? (
              <Loading message="Loading recent sales..." />
            ) : (
              <ListingsCarousel listings={soldListings} limit={10} />
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section-navy" data-cms-page="listings" data-cms-section="cta">
        <div className="container">
          <div className="section-header">
            <h2 data-cms-field="heading">{cta.heading}</h2>
            <p style={{ color: 'rgba(255,255,255,0.8)' }} data-cms-field="description">
              {cta.description}
            </p>
            <div className="btn-group" style={{ marginTop: '2rem' }}>
              <Link
                to={cta.primaryCtaLink}
                className="btn btn-primary btn-lg"
                data-cms-field="primaryCtaText"
                data-cms-link-field="primaryCtaLink"
              >
                {cta.primaryCtaText}
              </Link>
              <Link
                to={cta.secondaryCtaLink}
                className="btn btn-outline-light btn-lg"
                data-cms-field="secondaryCtaText"
                data-cms-link-field="secondaryCtaLink"
              >
                {cta.secondaryCtaText}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Listings;
